import { DateTime } from "luxon";
import { Badge, BadgeSize, BadgeVariant, Show } from "shared/components";
import { TimeLineItem } from "shared/components/TimeLine";
import { PropertyManagementSystem } from "shared/types/partner";
import { INTEGRATION_LOGO } from "views/settings-integration/constants";
import { pmsDisplayName } from "../constants";
import { ActivityCard as ActivityCardType, ActivityChange } from "../types";
import {
  Edit02Icon,
  RedoIcon,
  SquareLock02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";

const formatOccurredAtParts = (value: string) => {
  const date = DateTime.fromISO(value);
  return {
    date: date.toFormat("MMM d, yyyy"),
    time: date.toFormat("h:mm a")
  };
};

const subjectLabel = (card: ActivityCardType) =>
  card.subject.type === "property" ? "Property" : "Unit";

const crossUnitCount = (card: ActivityCardType) =>
  new Set(card.changes.map((change) => change.unit?.id)).size;

const cardTitle = (card: ActivityCardType) => {
  if (card.bundle_type === "lockbox_code_generated")
    return `Lockbox code generated by ${card.actor_name}`;

  if (card.bundle_type === "cross_unit")
    return `${card.field_label} updated on ${crossUnitCount(card)} units via ${pmsDisplayName(card.pms_type)} sync`;

  return card.actor_type === "pmi_sync"
    ? `${subjectLabel(card)} details updated via ${pmsDisplayName(card.pms_type)} sync`
    : `${subjectLabel(card)} details updated by ${card.actor_name}`;
};

export const ChangeRow = ({ change }: { change: ActivityChange }) => {
  if (change.unit)
    return (
      <div className="flex items-center gap-2 text-sm text-content-400">
        <Badge
          variant={BadgeVariant.BASE}
          size={BadgeSize.SM}
          className="w-fit"
        >
          {change.unit.title}
        </Badge>
        <span className="font-medium">{change.old_display ?? "—"}</span>{" "}
        <span className="text-content-100">→</span>{" "}
        <span className="font-medium">{change.new_display ?? "—"}</span>
      </div>
    );

  if (change.old_display === null)
    return (
      <p className="text-sm text-content-400">
        {change.label}:{" "}
        <span className="font-medium">{change.new_display}</span>
      </p>
    );

  return (
    <p className="text-sm text-content-400">
      {change.label}:{" "}
      <span className="font-medium">{change.old_display ?? "—"}</span>{" "}
      <span className="text-content-100">→</span>{" "}
      <span className="font-medium">{change.new_display ?? "—"}</span>
    </p>
  );
};

const DateColumn = ({ value }: { value: string }) => {
  const { date, time } = formatOccurredAtParts(value);
  return (
    <div className="pr-9 text-right">
      <p className="ml-auto w-fit whitespace-nowrap text-sm font-medium text-content-1000">
        {date}
      </p>
      <p className="ml-auto w-fit whitespace-nowrap text-xs text-content-400">
        {time}
      </p>
    </div>
  );
};

const PmsLogoContent = ({
  logo,
  pmsType
}: {
  logo?: (typeof INTEGRATION_LOGO)[PropertyManagementSystem];
  pmsType: string | null;
}) => {
  if (logo?.Icon) return <logo.Icon className="size-5" />;
  if (logo?.src)
    return (
      <img
        src={logo.src}
        alt={pmsType ?? undefined}
        className="size-5 object-contain"
      />
    );
  return null;
};

const bulletIcon = (
  actorType: ActivityCardType["actor_type"],
  bundleType: ActivityCardType["bundle_type"]
) => {
  if (bundleType === "lockbox_code_generated") return SquareLock02Icon;
  if (actorType === "pmi_sync") return RedoIcon;
  return Edit02Icon;
};

const Bullet = ({
  actorType,
  pmsType,
  bundleType
}: {
  actorType: ActivityCardType["actor_type"];
  pmsType: string | null;
  bundleType: ActivityCardType["bundle_type"];
}) => {
  const logo =
    actorType === "pmi_sync" && pmsType
      ? INTEGRATION_LOGO[pmsType as PropertyManagementSystem]
      : undefined;

  return (
    <div className="-ml-5 relative flex h-10 min-w-10 shrink-0 items-center justify-center rounded-full bg-background-900">
      <HugeiconsIcon
        icon={bulletIcon(actorType, bundleType)}
        strokeWidth={1.5}
        size={20}
      />
      <Show if={actorType === "pmi_sync"}>
        <div
          className="absolute bottom-0.5 right-0.5 flex size-5 items-center justify-center overflow-hidden rounded-full border border-background-900"
          style={{ backgroundColor: logo?.bg || "#3777BC" }}
        >
          <PmsLogoContent logo={logo} pmsType={pmsType} />
        </div>
      </Show>
    </div>
  );
};

export const ActivityCard = ({ card }: { card: ActivityCardType }) => (
  <TimeLineItem
    leftSlot={<DateColumn value={card.occurred_at} />}
    bullet={
      <Bullet
        actorType={card.actor_type}
        pmsType={card.pms_type}
        bundleType={card.bundle_type}
      />
    }
    wrapperClassName="mt-8 items-start"
    childrenClassName="w-full pl-4"
  >
    <div className="space-y-2">
      <Show if={!!card.subject.unit}>
        <Badge
          variant={BadgeVariant.BASE}
          size={BadgeSize.SM}
          className="w-fit"
        >
          {card.subject.unit?.title}
        </Badge>
      </Show>
      <p className="text-sm font-bold text-content-1000">{cardTitle(card)}</p>
    </div>

    <Show if={card.changes.length > 0}>
      <div className="space-y-2 pt-2">
        {card.changes.map((change) => (
          <ChangeRow
            key={`${card.id}-${change.field}-${change.unit?.id ?? "self"}`}
            change={change}
          />
        ))}
      </div>
    </Show>
  </TimeLineItem>
);
